import { createSystem, defaultConfig, defineConfig, defineRecipe, defineSlotRecipe } from '@chakra-ui/react'
import { breadcrumbAnatomy, dialogAnatomy, fieldAnatomy, listAnatomy, switchAnatomy, tableAnatomy, toastAnatomy } from '@chakra-ui/react/anatomy'

const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        primaryAccent: { value: 'white' },
        link: { value: '#2a8de8' },
        label: {
          primary: { value: '#1D1D21' },
          secondary: { value: '#5D5D61' },
          tertiary: { value: '#B8B8BD' },
          white: { value: '#FFFFFF' },
          black: { value: '#000000' },
          link: { value: '#2A8DE8' },
          accent: { value: '#E94841' },
          line: { value: '#52FF94' },
        },
        bg: {
          primary: { value: '#FFFFFF' },
          secondary: { value: '#F3F3F8' },
          tertiary: { value: '#EBEBF0' },
          footer: { value: '#222222' },
          pointBackTag: { value: '#029CF2' },
          guide: { value: '#B02D23' },
          guideAccent: { value: '#FCD502' },
          guideSubAccent: { value: '#D2352A' },
          blueGray: { value: '#4C636E' },
        },
        divider: {
          dark: { value: '#B8B8BD' },
          light: { value: '#DCDCE1' },
          white: { value: '#FFFFFF' },
        },
        primary: {
          main: { value: '#FD4238' },
          50: { value: '#FFEBEF' },
          100: { value: '#FFCED3' },
          200: { value: '#F49B9C' },
          300: { value: '#EC7475' },
          400: { value: '#F75352' },
          500: { value: '#FD4238' },
          600: { value: '#EE3737' },
          700: { value: '#DC2D31' },
          800: { value: '#CF252A' },
          900: { value: '#C0161D' },
        },
        secondary: {
          main: { value: '#FEC300' },
          50: { value: '#FFF8E1' },
          100: { value: '#FEEDB3' },
          200: { value: '#FEE182' },
          300: { value: '#FED64E' },
          400: { value: '#FECC26' },
          500: { value: '#FEC300' },
          600: { value: '#FEB500' },
          700: { value: '#FEA200' },
          800: { value: '#FF9100' },
          900: { value: '#FF7100' },
        },
        gray: {
          50: { value: '#F9F9FE' },
          100: { value: '#F3F3F8' },
          200: { value: '#EBEBF0' },
          300: { value: '#EDEDED' },
          400: { value: '#B8B8BD' },
          500: { value: '#99999E' },
          600: { value: '#707075' },
          700: { value: '#5D5D61' },
          800: { value: '#3E3E42' },
          900: { value: '#1D1D21' },
        },
        social: {
          facebook: { value: '#1877f2' },
          x: { value: '#1DA1F2' },
          line: { value: '#00C300' },
        },
        externalPayment: {
          discount: { value: '#FF0B00' },
          cardInputBorder: { value: '#E0E0E0' },
          cardInputBorderHover: { value: '#B8B8BD' },
          cardInputBorderFocus: { value: '#2A8DE8' },
          cardInputPlaceholder: { value: '#AAAAAA' },
          cardInputText: { value: '#222222' },
        },
        externalPaymentLogin: {
          inputBorder: { value: '#888888' },
          inputPlaceholder: { value: '#AAAAAA' },
          inputSeparator: { value: '#E0E0E0' },
        },
      },
      sizes: {
        container: {
          md: { value: '800px' },
        },
      },
      fontSizes: {
        '3xs': { value: '11px' },
        '2xs': { value: '12px' },
        xs: { value: '13px' },
        sm: { value: '14px' },
        md: { value: '16px' },
        lg: { value: '18px' },
        xl: { value: '20px' },
        '2xl': { value: '24px' },
        '3xl': { value: '32px' },
        '4xl': { value: '36px' },
        '5xl': { value: '42px' },
      },
    },
    semanticTokens: {
      colors: {
        primary: {
          solid: { value: '{colors.primary.500}' },
          contrast: { value: 'white' },
          fg: { value: '{colors.primary.500}' },
          border: { value: '{colors.primary.500}' },
          muted: { value: '{colors.primary.500}' },
          subtle: { value: '{colors.primary.50}' },
        },
        secondary: {
          solid: { value: '{colors.secondary.500}' },
          contrast: { value: '{colors.gray.900}' },
          fg: { value: '{colors.secondary.500}' },
          border: { value: '{colors.secondary.500}' },
          muted: { value: '{colors.secondary.500}' },
          subtle: { value: '{colors.secondary.50}' },
        },
        gray: {
          solid: { value: '{colors.gray.500}' },
        },
      },
    },
    recipes: {
      heading: defineRecipe({
        variants: {
          size: {
            xs: { fontSize: 'sm' },
            sm: { fontSize: 'md' },
            md: { fontSize: 'xl' },
            lg: { fontSize: '3xl' },
            xl: { fontSize: '4xl' },
          },
        },
      }),
      container: defineRecipe({
        base: {
          maxWidth: 'prose',
        },
      }),
      input: defineRecipe({
        variants: {
          size: {
            md: {
              textStyle: 'md',
              px: '4',
              borderRadius: 'md',
            },
          },
        },
      }),
      icon: defineRecipe({
        base: {
          width: '1em',
          height: '1em',
        },
      }),
      link: defineRecipe({
        base: {
          fontWeight: 'bold',
          display: 'inline',
          focusRing: 'none',
        },
        variants: {
          variant: {
            plain: {
              color: 'link',
              textDecoration: 'underline',
              _hover: { textDecoration: 'none' },
            },
          },
        },
      }),
      button: defineRecipe({
        base: {
          borderRadius: '4px',
          boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
          fontWeight: 'semibold',
          flexShrink: '1',
          focusRing: 'none',
        },
        defaultVariants: {
          variant: 'subtle',
        },
        variants: {
          variant: {
            solid: {
              bg: 'colorPalette.500',
              _hover: {
                bg: 'colorPalette.600',
              },
            },
            subtle: {
              _hover: {
                bg: 'colorPalette.200',
              },
            },
            outline: {
              _hover: {
                bg: 'colorPalette.50',
              },
            },
            ghost: {
              border: 'none',
              boxShadow: 'none',
            },
            link: {
              border: 'none',
              boxShadow: 'none',
            },
            unstyled: {
              boxShadow: 'none',
            },
          },
          size: {
            '2xs': { h: '6', minW: '6', px: '2', gap: '1', textStyle: 'xs' },
            xs: { h: '8', minW: '8', px: '2.5', gap: '1', textStyle: 'xs' },
            sm: { h: '9', minW: '9', px: '3.5', gap: '2', textStyle: 'sm' },
            md: { h: '10', minW: '10', px: '4', gap: '2', textStyle: 'sm' },
            lg: { h: '11', minW: '11', px: '5', gap: '3', textStyle: 'md' },
            xl: { h: '12', minW: '12', px: '5', gap: '2.5', textStyle: 'md' },
          },
        },
      }),
      separator: defineRecipe({
        base: {
          borderColor: 'divider.light',
        },
      }),
    },
    slotRecipes: {
      dialog: defineSlotRecipe({
        slots: dialogAnatomy.keys(),
        base: {
          body: {
            color: 'label.primary',
            fontSize: 'md',
          },
        },
      }),
      list: defineSlotRecipe({
        slots: listAnatomy.keys(),
        base: {
          root: {
            paddingInlineStart: '1.25rem',
          },
        },
        variants: {
          variant: {
            marker: {
              item: {
                _marker: { color: 'inherit' },
              },
            },
          },
        },
      }),
      breadcrumb: defineSlotRecipe({
        slots: breadcrumbAnatomy.keys(),
        base: {
          list: {
            color: 'gray.900',
            alignItems: 'center',
          },
          link: {
            focusRing: 'none',
            display: 'inline',
          },
          currentLink: {
            focusRing: 'none',
            display: 'inline',
          },
          separator: {
            marginInlineEnd: '0',
          },
        },
        variants: {
          size: {
            md: {
              list: {
                gap: '0',
              },
            },
          },
          variant: {
            plain: {
              link: {
                color: 'gray.900',
                _hover: { color: 'gray.900' },
              },
              currentLink: {
                color: 'gray.900',
              },
            },
          },
        },
      }),
      field: defineSlotRecipe({
        slots: fieldAnatomy.keys(),
        base: {
          label: {
            textStyle: 'md',
            _required: {
              '&::after': {
                content: '" *"',
                color: 'fg.error',
              },
            },
          },
        },
      }),
      table: defineSlotRecipe({
        slots: tableAnatomy.keys(),
        base: {
          columnHeader: {
            fontWeight: 'bold',
          },
        },
        variants: {
          variant: {
            simple: {
              header: { borderTop: '1px solid', borderColor: 'divider.light' },
              columnHeader: {
                color: 'label.primary',
                fontSize: 'sm',
                fontWeight: 'bold',
                bg: 'bg.secondary',
                borderBottomWidth: '1px',
                borderColor: 'divider.light',
                borderEndWidth: '1px',
                borderEndStyle: 'solid',
                borderEndColor: 'divider.light',
                '&:last-child': { borderEndWidth: '0' },
              },
              cell: {
                fontSize: 'sm',
                fontWeight: 'bold',
                borderBottomWidth: '1px',
                borderColor: 'divider.light',
                borderEndWidth: '1px',
                borderEndStyle: 'solid',
                borderEndColor: 'divider.light',
                '&:last-child': { borderEndWidth: '0' },
              },
            },
          },
        },
      }),
      toast: defineSlotRecipe({
        slots: toastAnatomy.keys(),
        base: {
          root: {
            // Chakra UI v3 のデフォルトは warning / success / error にしか配色がないため info を補う
            '&[data-type=info]': {
              bg: 'blue.solid',
              color: 'blue.contrast',
              '--toast-trigger-bg': '{white/10}',
              '--toast-border-color': '{white/40}',
            },
          },
        },
      }),
      switch: defineSlotRecipe({
        slots: switchAnatomy.keys(),
        base: {
          control: {
            '--focus-ring-width': '0px',
          },
        },
      }),
      tabs: {
        base: {
          trigger: {
            justifyContent: 'center',
            color: 'gray.900',
          },
        },
        variants: {
          variant: {
            line: {
              trigger: {
                color: 'gray.900',
                _selected: {
                  color: 'primary.500',
                  _horizontal: {
                    '--indicator-color': 'colors.primary.500',
                    '--indicator-thickness': '4px',
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  globalCss: {
    'html, body': {
      height: '100%',
      bg: 'gray.50',
      color: 'gray.900',
    },
    '#__next': {
      height: '100%',
      bg: 'gray.50',
    },
    // Chakra UI v3 既定の選択色 (colorPalette.emphasized/80 = gray.300) は
    // 本テーマの gray.300 (#EDEDED) だと白背景と見分けがつかないため、選択範囲を明示する
    '*::selection': {
      bg: 'blue.200',
      color: 'gray.900',
    },
  },
})

export const system = createSystem(defaultConfig, config)
